import React, { useEffect, useState } from 'react'
import { formatPhone } from '@/lib/format'
import { shallow } from 'zustand/shallow'
import { Phone } from 'lucide-react'
import { useCallStore } from '@/stores/callStore'

/**
 * Phase 2D — iOS-style "tap to return to call" pill.
 *
 * Visibility rules:
 *  - Mounted at the app root (Layout.tsx) so it renders on every route.
 *  - Visible whenever a call is in progress (`callStatus` is not 'idle' or
 *    'ended') AND the user has navigated away from the softphone (i.e.
 *    `softphoneExpanded === false`). When the softphone is open the user
 *    is already in the active-call view, so the pill would be redundant.
 *  - Hidden during ringing too — the incoming-call overlay handles that
 *    full-screen on mobile and via IncomingCallToast on desktop.
 *
 * Tap behaviour:
 *  - Sets `softphoneExpanded = true` (re-opens the active-call view).
 *  - Dispatches the existing `softphone:focus` window event so the
 *    Softphone widget can scroll itself into view on desktop.
 *
 * The Twilio Device lives in the Zustand `useCallStore` (module-level),
 * and `<Softphone>` is mounted in `Layout.tsx` outside the route tree —
 * so the call already survives navigation and tab changes today. This
 * pill is the visible affordance to return to it.
 */
export function ActiveCallPill(): React.ReactElement | null {
  const { callStatus, callerInfo, callStartTime, softphoneExpanded, setSoftphoneExpanded } =
    useCallStore(
      (s) => ({
        callStatus: s.callStatus,
        callerInfo: s.callerInfo,
        callStartTime: s.callStartTime,
        softphoneExpanded: s.softphoneExpanded,
        setSoftphoneExpanded: s.setSoftphoneExpanded,
      }),
      shallow,
    )

  // Only count "active" states — exclude 'ringing' (handled by overlay)
  // and 'idle'/'ended' (no call to return to).
  const isOnCall =
    callStatus === 'connecting' || callStatus === 'connected'

  // Hide while the softphone is already open — the user is already in
  // the active-call view; a top pill would just duplicate UI.
  const visible = isOnCall && !softphoneExpanded

  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (!visible || !callStartTime) {
      setElapsed(0)
      return
    }
    setElapsed(Math.floor((Date.now() - callStartTime) / 1000))
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - callStartTime) / 1000))
    }, 1000)
    return () => clearInterval(interval)
  }, [visible, callStartTime])

  if (!visible) return null

  const mins = Math.floor(elapsed / 60)
  const secs = elapsed % 60
  const durationLabel =
    callStartTime != null
      ? `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
      : '· · ·'

  const handleClick = () => {
    setSoftphoneExpanded(true)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('softphone:focus'))
    }
  }

  const callerLabel = callerInfo?.name || (callerInfo?.number ? formatPhone(callerInfo.number) : 'Active call')

  return (
    <button
      type="button"
      onClick={handleClick}
      className="ds-call-pill animate-fade-up"
      aria-label={`Return to call with ${callerLabel}`}
      data-testid="active-call-pill"
    >
      <span className="ds-call-pill-dot" aria-hidden="true" />
      <Phone style={{ width: 12, height: 12, color: 'var(--midnight-mint)' }} aria-hidden="true" />
      <span
        style={{
          maxWidth: 160,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {callerLabel}
      </span>
      <span
        style={{
          color: 'var(--midnight-text2, rgb(var(--fg-rgb) / 0.60))',
          fontVariantNumeric: 'tabular-nums',
          fontSize: 12,
        }}
      >
        {durationLabel}
      </span>
    </button>
  )
}
